import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { tenantLlmConfigService } from "../services/tenantLlmConfigService";
import { tenantLlmConfigDisplayId } from "./TenantLlmConfigCommonStore";
import { TenantLlmConfig } from "../../../api/graphql";
import { llmProviderDisplayId } from "../../llmProvider/store/LlmProviderCommonStore";
import { llmProviderService } from "../../llmProvider/services/llmProviderService";
import { InitializeParams } from "../../../store/types";

export class TenantLlmConfigViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = tenantLlmConfigService;
  tenantLlmConfigInstance: TenantLlmConfig | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  providerService = llmProviderService;
  providerSearchValue: string = "";
  providerIdValue: string = "";
  providerSelectedValue: { displayId: string; id: string } | null = null;
  modelValue: string = "";
  baseUrlValue: string = "";
  enabledValue: boolean = false;
  priorityValue: number = 0;
  maxTokensValue: number | undefined = undefined;
  temperatureValue: number | undefined = undefined;
  debugFullModeValue: boolean = false;

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadTenantLlmConfigById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.tenantLlmConfigOne({ id });
    this.tenantLlmConfigInstance = result.tenantLlmConfigOne;
    if (this.tenantLlmConfigInstance) {
      this.loadValues(this.tenantLlmConfigInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.tenantLlmConfigInstance) {
      return tenantLlmConfigDisplayId(
        this.tenantLlmConfigInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.TenantLlmConfigSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "provider") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.llmProviderOne({
          id: params.returnData.resultId,
        });
        const llmProvider = result.llmProviderOne;
        this.providerSelectedValue = llmProvider;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get providerValue() {
    if (this.providerSelectedValue) {
      return {
        id: this.providerSelectedValue.id,
        displayId: llmProviderDisplayId(
          this.providerSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.tenantLlmConfigInstance = null;
    this.providerSearchValue = "";
    this.providerIdValue = "";
    this.modelValue = "";
    this.baseUrlValue = "";
    this.enabledValue = false;
    this.priorityValue = 0;
    this.maxTokensValue = undefined;
    this.temperatureValue = undefined;
    this.debugFullModeValue = false;
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelTenantLlmConfig() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return this.rootStore.authStore.hasPermissions("tenantLlmConfig_update");
  }

  editTenantLlmConfig() {
    const id = this.tenantLlmConfigInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/tenantLlmConfig/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printTenantLlmConfig(handlePrint: () => void): void {
    handlePrint();
  }

  *closeTenantLlmConfig(): any {
    yield this.close();
  }

  loadValues(instance: TenantLlmConfig) {
    this.idValue = instance.id;
    this.providerSelectedValue = instance.provider;
    this.modelValue = instance.model;
    this.baseUrlValue = instance.baseUrl || "";
    this.enabledValue = instance.enabled;
    this.priorityValue = instance.priority;
    this.maxTokensValue = instance.maxTokens || undefined;
    this.temperatureValue = instance.temperature || undefined;
    this.debugFullModeValue = instance.debugFullMode;
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadTenantLlmConfigById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
